/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useEffect } from 'react';

import { TableOfContent } from '../../../typings/interfaces';
import languages from '../../../utils/constants/languages';
import { Select, Text, Flex, Icon } from '@mobilearch/react-ui';
import tableOfContent from '../../../utils/constants/tableOfContent';
import { Nav } from './styles';

interface SideMenuProps {
  handleTranslation(translation: string): void;
  handleNumberPage(page: number): void;
  translation: TableOfContent;
  active: boolean;
}

const SideMenu = ({ active, translation, handleTranslation, handleNumberPage }: SideMenuProps): React.ReactElement => {
  const [search, setSearch] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [activeSections, setActiveSections] = useState<number[]>([]);

  const showSection = (event: Event, index: number) => {
    event.stopPropagation();
    let updatedList: number[] = [];
    if (activeSections.length === 0) {
      updatedList.push(index);
    } else if (activeSections.includes(index)) {
      updatedList = activeSections.filter((section) => section !== index);
    } else {
      updatedList = [...activeSections, index];
    }
    setActiveSections(updatedList);
  };

  const goToPage = (event: Event, pageNumber: number) => {
    event.stopPropagation();
    handleNumberPage(pageNumber);
  };

  const openAllSections = (action: 'open' | 'close') => {
    setShowSearch(!showSearch);
    if (action === 'close') {
      setSearch('');
      setActiveSections([]);
    } else {
      setActiveSections(translation.sections.map((_, index) => index));
    }
  };

  useEffect(() => {
    const subSections = document.getElementsByClassName('sub-section') as unknown as Element[];
    const regex = new RegExp(search, 'ig');
    if (subSections.length > 0) {
      for (let i = 0; i < subSections.length; i++) {
        if (subSections[i].innerHTML?.includes('<mark>')) {
          subSections[i].innerHTML = subSections[i].innerHTML.replace(/<mark>/g, '');
          subSections[i].innerHTML = subSections[i].innerHTML.replace(/<\/mark>/g, '');
        }
        if (search.length > 1 && subSections[i].textContent?.toLowerCase().includes(search.toLowerCase())) {
          const initIndex = subSections[i].innerHTML.search(regex);
          const lastIndex = initIndex + search.length;
          subSections[i].innerHTML = subSections[i].innerHTML.replace(
            regex,
            `<mark>${subSections[i].innerHTML.substring(initIndex, lastIndex)}</mark>`,
          );
        }
      }
    }
  }, [search]);

  return (
    <Nav className={`side-menu ${active && 'side-menu-active'}`}>
      {showSearch ? (
        <Flex container justifyContent="space-between" className="search-container" alignItems="center">
          <input
            autoFocus
            className="search-input"
            value={search}
            onChange={({ currentTarget }) =>
              setSearch(!/[*\[\]\\/()\?]/g.test(currentTarget.value) ? currentTarget.value : search)
            }
          />
          <Icon onClick={() => openAllSections('close')}>cancel</Icon>
        </Flex>
      ) : (
        <Flex container justifyContent="space-between" className="title-container" alignContent="center">
          <Text variant="title4" className="title">
            {translation.label}
          </Text>
          <Icon onClick={() => openAllSections('open')}>search</Icon>
        </Flex>
      )}
      <ul>
        {translation.sections.map((section, index) => (
          <li key={index} onClick={(e) => showSection(e as unknown as Event, index)} className="section">
            <Flex container justifyContent="space-between">
              <Text variant="title5">{section.name}</Text>
              <Icon>{activeSections.includes(index) ? 'expand_less' : 'expand_more'}</Icon>
            </Flex>
            <ul className={`${activeSections.includes(index) ? 'show-sub-section-list' : ''} sub-section-list`}>
              {section.subSections.map((subSection, j) => (
                <div key={j}>
                  <li onClick={(e) => goToPage(e as unknown as Event, subSection.page)}>
                    <Text className="sub-section" variant="caption">
                      {subSection.name}
                    </Text>
                  </li>
                  {index === translation.sections.length - 1 && j === section.subSections.length - 1 && (
                    <li>
                      <Text variant="caption">
                        <a href={translation.hopPocketLink} target="_blank">
                          {translation.hopPocket}
                        </a>
                      </Text>
                    </li>
                  )}
                </div>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <Select
        onChange={(_, value) => handleTranslation(value || translation.language)}
        value={translation.language}
        options={languages}
        closedIcon="languages"
        openedIcon="languages"
      />
    </Nav>
  );
};

SideMenu.defaultProps = {
  translation: tableOfContent[0],
  active: false,
  handleTranslation: () => {},
  handleNumberPage: () => {},
};

export default SideMenu;
